/**
 * One legajo.
 *
 * It opens on the conclusion — the recommendation and what still needs a human — because that is
 * the order the analyst's questions arrive in. The figures, the outside sources, the documents and
 * the technical trace sit behind it, in that order.
 *
 * The header carries one primary action, and the system decides what it says. Nobody is asked
 * which pipeline step to resume from.
 */

import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, friendlyError } from "../api/client";
import { DECISIONS, STEP_LABELS, label } from "../lib/labels";
import {
  caseRisk,
  caseState,
  decisionChip,
  gradeChip,
  isRunning,
  nextAction,
  runProgress,
} from "../lib/model";
import { Button, Chip, ErrorBanner, PageHeader, Spinner } from "../components/ui";
import { fallbackReportName, saveFile } from "../lib/download";
import EvidenceDrawer, { type EvidenceRequest } from "../components/EvidenceDrawer";
import Summary from "./case/Summary";
import Score from "./case/Score";
import Figures from "./case/Figures";
import Sources from "./case/Sources";
import Documents from "./case/Documents";
import Trace from "./case/Trace";
import Notes from "./case/Notes";
import CaseSettings from "./case/CaseSettings";

const TABS = [
  { key: "resumen", label: "Resumen" },
  { key: "score", label: "Calificación" },
  { key: "cifras", label: "Cifras" },
  { key: "fuentes", label: "Fuentes externas" },
  { key: "documentos", label: "Documentos" },
  { key: "notas", label: "Notas e historial" },
  { key: "traza", label: "Traza técnica" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

export default function CaseView({ caseId }: { caseId: string }) {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [evidence, setEvidence] = useState<EvidenceRequest | null>(null);
  const [settings, setSettings] = useState(false);

  const tab = (TABS.find((t) => t.key === params.get("vista"))?.key ?? "resumen") as TabKey;

  const kase = useQuery({
    queryKey: ["case", caseId],
    queryFn: () => api.getCase(caseId),
    // While a legajo is being analysed the header and the progress bar have to move; when it is
    // idle, polling it every few seconds would be noise.
    refetchInterval: (query) => (isRunning(query.state.data) ? 2500 : false),
  });

  const start = useMutation({
    mutationFn: (fromStep: string | null) => api.startRun(caseId, fromStep),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["case", caseId] });
      await queryClient.invalidateQueries({ queryKey: ["cases"] });
    },
  });

  if (kase.isError) return <ErrorBanner {...friendlyError(kase.error)} />;
  if (!kase.data) return <Spinner />;
  const data = kase.data;

  const state = caseState(data);
  const risk = caseRisk(data);
  const grade = gradeChip(data.score);
  const action = nextAction(data);
  const run = data.latest_run;
  const progress = runProgress(data);

  function openEvidence(request: EvidenceRequest) {
    setEvidence(request);
  }

  function go(key: TabKey) {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("vista", key);
        return next;
      },
      { replace: true },
    );
  }

  return (
    <div className={evidence ? "pr-[46vw]" : undefined}>
      <PageHeader
        breadcrumb={
          <Link to="/legajos" className="hover:text-ink hover:underline underline-offset-2">
            ← Cartera de legajos
          </Link>
        }
        title={data.legal_name}
        subtitle={<span className="tabular-nums">CUIT {data.cuit}</span>}
        chips={
          <>
            <Chip text={state.text} tone={state.tone} />
            {risk ?
              <Chip text={risk.text} tone={risk.tone} />
            : null}
            {grade ?
              <button
                onClick={() => go("score")}
                title={
                  data.score?.partial ?
                    "La calificación se apoya en información parcial — mirá la cobertura"
                  : "Ver cómo se calculó"
                }
              >
                <Chip text={`Calificación ${grade.text}`} tone={grade.tone} />
              </button>
            : null}
            {data.decision ?
              <Chip
                text={`Decisión: ${label(DECISIONS, data.decision.outcome)}`}
                tone={decisionChip(data.decision)!.tone}
              />
            : null}
            {data.archived_at ?
              <Chip text="Archivado" tone="neutral" />
            : null}
            <span className="text-muted">
              {data.assigned_to_name ? `A cargo de ${data.assigned_to_name}` : "Sin asignar"}
            </span>
            <button
              onClick={() => setSettings(true)}
              className="text-primary underline underline-offset-2"
            >
              Editar
            </button>
          </>
        }
        actions={
          <PrimaryAction
            caseId={caseId}
            cuit={data.cuit}
            action={action}
            onStart={(fromStep) => start.mutate(fromStep)}
            pending={start.isPending}
          />
        }
      />

      {start.isError ?
        <div className="mt-4">
          <ErrorBanner {...friendlyError(start.error)} />
        </div>
      : null}

      <div className="my-5 border-b border-line" />

      {run?.status === "failed" ?
        <div className="mb-5">
          <ErrorBanner
            message={`El análisis se interrumpió en «${STEP_LABELS[run.current_step ?? ""] ?? "un paso"}». Podés reintentarlo: se conserva todo lo ya procesado.`}
            detail={run.error ?? undefined}
          />
        </div>
      : null}

      {progress && isRunning(data) ?
        // El análisis avanza solo, sin que nadie toque nada: un lector de pantalla no se entera de
        // que el legajo pasó de «Extrayendo» a «Analizando» salvo que la región lo anuncie.
        <div className="mb-6" role="status" aria-live="polite">
          <div className="mb-1.5 flex items-baseline justify-between text-sm">
            <span>{progress.current}</span>
            <span className="text-muted">
              Paso {Math.min(progress.done + 1, progress.total)} de {progress.total}
            </span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-line">
            <div
              className="h-full rounded-full bg-primary transition-[width] duration-500"
              style={{ width: `${Math.round(progress.fraction * 100)}%` }}
            />
          </div>
        </div>
      : null}

      <div className="flex gap-6 border-b border-line" role="tablist">
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            role="tab"
            aria-selected={tab === key}
            onClick={() => go(key)}
            className={`-mb-px border-b-2 py-2 text-sm font-medium transition-colors ${
              tab === key ? "border-primary text-ink" : (
                "border-transparent text-muted hover:text-ink"
              )
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="pt-6">
        {tab === "resumen" ?
          <Summary kase={data} onEvidence={openEvidence} onOpenScore={() => go("score")} />
        : null}
        {tab === "score" ?
          <Score caseId={caseId} onEvidence={openEvidence} />
        : null}
        {tab === "cifras" ?
          <Figures caseId={caseId} onEvidence={openEvidence} />
        : null}
        {tab === "fuentes" ?
          <Sources caseId={caseId} runId={run?.id ?? null} />
        : null}
        {tab === "documentos" ?
          <Documents kase={data} />
        : null}
        {tab === "notas" ?
          <Notes caseId={caseId} />
        : null}
        {tab === "traza" ?
          <Trace kase={data} onResume={(step) => start.mutate(step)} />
        : null}
      </div>

      <EvidenceDrawer request={evidence} onClose={() => setEvidence(null)} />
      {settings ?
        <CaseSettings kase={data} onClose={() => setSettings(false)} />
      : null}
    </div>
  );
}

function PrimaryAction({
  caseId,
  cuit,
  action,
  onStart,
  pending,
}: {
  caseId: string;
  cuit: string;
  action: ReturnType<typeof nextAction>;
  onStart: (fromStep: string | null) => void;
  pending: boolean;
}) {
  const reports = useQuery({
    queryKey: ["reports", caseId],
    queryFn: () => api.listReports(caseId),
    enabled: action.key === "report",
  });

  async function download() {
    const list = reports.data ?? [];
    const report = list.find((r) => r.format === "docx") ?? list[0];
    if (!report) return;
    saveFile(await api.downloadReport(report.id), fallbackReportName(cuit, report.format));
  }

  if (action.key === "report") {
    return (
      <>
        <Button variant="primary" onClick={download} disabled={!reports.data?.length}>
          Descargar informe
        </Button>
        <Button onClick={() => onStart(null)} disabled={pending}>
          Volver a analizar
        </Button>
      </>
    );
  }

  return (
    <>
      <Button
        variant="primary"
        disabled={!action.enabled || pending}
        onClick={() => {
          if (action.key === "analyze" || action.key === "retry") onStart(action.fromStep);
        }}
      >
        {pending ? "Enviando…" : action.label}
      </Button>
      {action.hint ?
        <p className="max-w-56 text-right text-xs text-muted">{action.hint}</p>
      : null}
    </>
  );
}
